import { useState } from 'react'
import type { JSX } from 'react'

import { format, isSameYear } from 'date-fns'

import { CalendarIcon, Flex, IconButton, XIcon } from '@buffer-mono/popcorn'
import type { ButtonProps } from '@buffer-mono/popcorn'

import { DateRangePopover } from '~publish/components/DateRangePicker'
import { FilterButton } from '~publish/components/FilterButton'

export type DateRange = { startDate: Date; endDate: Date }

/** Drops the year from the start date when both ends fall in the current year. */
function formatDateRange(startDate: Date, endDate: Date): string {
  const now = new Date()
  const dateFormat =
    isSameYear(startDate, now) && isSameYear(endDate, now)
      ? 'MMM d'
      : 'MMM d, yyyy'
  return `${format(startDate, dateFormat)} – ${format(endDate, 'MMM d, yyyy')}`
}

type FilterByDateRangeProps = Pick<ButtonProps, 'size' | 'variant'> & {
  /** The applied range, or `undefined` when the filter isn't narrowing by date. */
  value: DateRange | undefined
  onApply: (startDate: Date, endDate: Date) => void
  /** Removes the date filter entirely. Only rendered once a range is applied. */
  onClear: () => void
  weekStartsOn?: 0 | 1
  className?: string
}

/**
 * Date-range filter chip, reusing Insights' "Custom" range picker
 * (`DateRangePopover`) behind a `FilterButton` trigger instead of Insights'
 * preset toggle — Board only ever wants the custom picker.
 *
 * Uses `manualTrigger` so the chip and the clear button are true siblings
 * inside the popover's anchor, each with their own onClick — nesting the
 * clear button inside the chip's own onClick-opens-the-popover span would
 * make clearing also reopen the popover.
 */
export const FilterByDateRange = ({
  size = 'medium',
  variant = 'tertiary',
  value,
  onApply,
  onClear,
  weekStartsOn,
  className,
}: FilterByDateRangeProps): JSX.Element => {
  const [open, setOpen] = useState(false)

  return (
    <DateRangePopover
      open={open}
      onOpenChange={setOpen}
      initialRange={value}
      weekStartsOn={weekStartsOn}
      onApply={onApply}
      manualTrigger
    >
      <Flex gap="2xs" align="center" className={className}>
        <FilterButton
          icon={<CalendarIcon />}
          count={value ? 1 : 0}
          variant={variant}
          size={size}
          hideChevron={Boolean(value)}
          onClick={(): void => setOpen(true)}
        >
          {value ? formatDateRange(value.startDate, value.endDate) : 'Date'}
        </FilterButton>
        {value && (
          <IconButton
            variant={variant}
            size={size}
            label="Clear date filter"
            tooltip="Clear date filter"
            onClick={onClear}
          >
            <XIcon />
          </IconButton>
        )}
      </Flex>
    </DateRangePopover>
  )
}
